import { DropdownWithLabel } from '@blueprint/dropdown';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import {
    DropdownWrapper,
    FiltersLabel,
    LeftWrapper,
    RightWrapper
} from './Options.styles';

const PriceFrequencyWrapper = styled.div(
    ({ theme: { spacing } }) => css`
        width: 100%;
        margin-bottom: ${spacing['16px']};
    `
);

const FilterWrapper = styled.div(
    ({ theme: { spacing } }) => css`
        margin: 0;
        width: 100%;
        padding-bottom: ${spacing['24px']};
    `
);

const priceRangeLabel = 'Price range';
const maxPriceLabel = 'Max price';
const minPriceLabel = 'Min price';
const priceFrequencyLabel = 'Price per';

const priceFrequencyOptions: { [key: PriceFrequency]: string } = {
    per_month: 'pcm',
    per_week: 'pcw'
};

const priceOptions = [
    '25',
    '50',
    '75',
    '100',
    '125',
    '150',
    '175',
    '200',
    '225',
    '250',
    '275',
    '300',
    '325',
    '350',
    '375',
    '400',
    '425',
    '450',
    '475',
    '500',
    '550',
    '600',
    '650',
    '700',
    '750',
    '800',
    '850',
    '900',
    '950',
    '1000',
    '1250',
    '1500',
    '1750',
    '2000',
    '2500',
    '3000',
    '3500',
    '4000',
    '4500',
    '5000',
    '7500'
];

type prices = typeof priceOptions[number];

export type PriceFrequency = '' | 'per_week' | 'per_month' | string;
export type PriceMin = prices;
export type PriceMax = prices;

type SelectPriceToRentProps = {
    value: {
        priceMin: PriceMin;
        priceMax: PriceMax;
        priceFrequency: PriceFrequency;
    };
    onChange: ({
        priceMin,
        priceMax,
        priceFrequency
    }: {
        priceMin: PriceMin;
        priceMax: PriceMax;
        priceFrequency: PriceFrequency;
    }) => void;
};

export const SelectPriceToRent = ({
    value,
    onChange
}: SelectPriceToRentProps) => {
    const priceMinOptions =
        value.priceMax !== ''
            ? priceOptions.slice(0, priceOptions.indexOf(value.priceMax) + 1)
            : priceOptions;

    const priceMaxOptions =
        value.priceMin !== ''
            ? priceOptions.slice(priceOptions.indexOf(value.priceMin))
            : priceOptions;

    const priceFrequency = priceFrequencyOptions[value.priceFrequency];

    return (
        <FilterWrapper>
            <FiltersLabel htmlFor="modal-price-filter">
                {priceRangeLabel}
            </FiltersLabel>
            <PriceFrequencyWrapper>
                <DropdownWithLabel
                    id="mobile_modal_price_frequency-filter"
                    data-testid="price_frequency-filter-select_modal_mobile"
                    onChange={(e) => {
                        onChange({
                            ...value,
                            priceFrequency: e.target.value
                        });
                    }}
                    value={value.priceFrequency}
                    name="priceFrequency"
                    label={priceFrequencyLabel}
                    aria-label="Price Frequency"
                    hasBorderRadius
                >
                    <option value="per_month">Month</option>
                    <option value="per_week">Week</option>
                </DropdownWithLabel>
            </PriceFrequencyWrapper>

            <DropdownWrapper>
                <LeftWrapper>
                    <DropdownWithLabel
                        onChange={(e) => {
                            onChange({
                                ...value,
                                priceMin: e.target.value
                            });
                        }}
                        value={value.priceMin}
                        name="price_min_monthly"
                        aria-label="Min Price monthly"
                        data-testid="min_price_monthly_modal_mobile"
                        id="price_min_monthly_mobile_modal"
                        label={minPriceLabel}
                        hasBorderRadius
                    >
                        <option value="">No min</option>
                        {priceMinOptions.map((priceMinOption, index) => {
                            const price = new Intl.NumberFormat('en-GB', {
                                style: 'currency',
                                currency: 'GBP',
                                maximumFractionDigits: 0,
                                minimumFractionDigits: 0
                            }).format(Number(priceMinOption));

                            return (
                                <option
                                    value={priceMinOption}
                                    key={`modal_price_min_${index}`}
                                >
                                    {price} {priceFrequency}
                                </option>
                            );
                        })}
                    </DropdownWithLabel>
                </LeftWrapper>
                <RightWrapper>
                    <DropdownWithLabel
                        onChange={(e) => {
                            onChange({
                                ...value,
                                priceMax: e.target.value
                            });
                        }}
                        value={value.priceMax}
                        name="price_max_monthly"
                        id="price_max_monthly_mobile_modal"
                        aria-label="Max Price monthly"
                        data-testid="max_price_monthly_modal_mobile"
                        label={maxPriceLabel}
                        hasBorderRadius
                    >
                        <option value="">No max</option>
                        {priceMaxOptions.map((priceMaxOption, index) => {
                            const price = new Intl.NumberFormat('en-GB', {
                                style: 'currency',
                                currency: 'GBP',
                                maximumFractionDigits: 0,
                                minimumFractionDigits: 0
                            }).format(Number(priceMaxOption));

                            return (
                                <option
                                    value={priceMaxOption}
                                    key={`modal_max_${index}`}
                                >
                                    {price} {priceFrequency}
                                </option>
                            );
                        })}
                    </DropdownWithLabel>
                </RightWrapper>
            </DropdownWrapper>
        </FilterWrapper>
    );
};
